import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardGenerateContinueByDateUX from "./CardGenerateContinueByDateUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import { 
  State,
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  GenerateContinueDataType,
  // TaskOptions,
  TableHeaderType,
} from "./sequence/GenerateContinueByDate";

// Serializer
import GenDrugContinueInterfaceLogSerializer from "issara-sdk/types/GenDrugContinueIssueLogSerializer_apps_TPD";

type CardGenerateContinueByDateProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence,
  GenerateContinueByDateSequnce: State["GenerateContinueByDateSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardGenerateContinueByDate = (props: CardGenerateContinueByDateProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "GenerateContinueByDate", restart: true })
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof GenerateContinueDataType;
    
    props.setProp(`GenerateContinueByDateSequence.generateContinueData.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`GenerateContinueByDateSequence.generateContinueData.date`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`GenerateContinueByDateSequence.paginatedItems.${key}`, value);

    handleFetch();
  };

  const handleGenerate = () => {
    props.runSequence({
      sequence: "GenerateContinueByDate",
      action: "GENERATE",
    });
  }

  const handleFetch = () => {
    props.runSequence({
      sequence: "GenerateContinueByDate",
      action: "FETCH",
    });
  }

  const handleDisabledGenerate = useMemo(() => {
    const date = props.GenerateContinueByDateSequnce?.generateContinueData?.date;

    return !(date);
  }, [
    props.GenerateContinueByDateSequnce?.generateContinueData?.date
  ]);

  // useMemo
  const continueList = useMemo(() => {
    return (props.GenerateContinueByDateSequnce?.continueList || []).map(
      (item: GenDrugContinueInterfaceLogSerializer) => {
        return {
          ...item,
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof GenDrugContinueInterfaceLogSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "generate_date") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              }  else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            }, {}
          ),
        }
      }
    )
  }, [
    props.GenerateContinueByDateSequnce?.continueList
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardGenerateContinueByDateUX
        onChangeData={handleChangeData}
        onChangeDate={handleChangeDate}
        generateContinueData={props.GenerateContinueByDateSequnce?.generateContinueData}
        // table
        tableHeaders={tableHeaders}
        continueList={continueList}
        paginatedItems={props.GenerateContinueByDateSequnce?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        generateButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleGenerate}
            disabled={handleDisabledGenerate}
            // data
            paramKey={`${CARD_NAME}_GENERATE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_GENERATE`]}
            // config
            color="green"
            title="Generate"
            name="GENERATE"
            size="medium"
          />
        }
        refreshButton={
          <ButtonLoadCheck
          // func
          setProp={props.setProp}
          onClick={handleFetch}
          // data
          paramKey={`${CARD_NAME}_FETCH`}
          buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_FETCH`]}
          // config
          icon="refresh"
          color="orange"
          name="FETCH"
          size="medium"
          />
        }
      />
    </>
  );
};

CardGenerateContinueByDate.displayName = "CardGenerateContinueByDate";

export default React.memo(CardGenerateContinueByDate);

const tableHeaders: TableHeaderType<GenDrugContinueInterfaceLogSerializer>[] = [
  {
    Header: "Generate Date",
    accessor: "generate_date",
    // width: 150,
  },
  {
    Header: "Status",
    accessor: "status_label",
    // width: 120,
  },
];